import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { FieldLabel, FieldMessage, useFieldSemantics } from './fieldSemantics';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  /** When the input is a password field, render a button to toggle masked/unmasked. */
  showPasswordToggle?: boolean;
  /**
   * Accessible name of the password visibility toggle. Pass the consuming screen's
   * TR/EN string. The name stays constant while toggling; the current state is
   * announced through `aria-pressed` (a changing name plus `aria-pressed` would
   * double-announce and contradict itself). Defaults to Turkish for backward compatibility.
   */
  passwordToggleLabel?: string;
}

const DEFAULT_PASSWORD_TOGGLE_LABEL = 'Şifreyi göster';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, className = '', id, type, showPasswordToggle, passwordToggleLabel = DEFAULT_PASSWORD_TOGGLE_LABEL, 'aria-describedby': ariaDescribedBy, 'aria-invalid': ariaInvalid, ...props }, ref) => {
    const field = useFieldSemantics({
      id,
      error,
      helperText,
      'aria-describedby': ariaDescribedBy,
      'aria-invalid': ariaInvalid,
    });
    const inputId = field.fieldId;
    const [visible, setVisible] = useState(false);

    const hasToggle = showPasswordToggle && type === 'password';
    const inputType = hasToggle && visible ? 'text' : type;

    return (
      <div className="w-full">
        {label && <FieldLabel htmlFor={inputId}>{label}</FieldLabel>}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={inputType}
            className={`
              block w-full px-3 py-2 border rounded-md bg-slate-900 text-white
              placeholder-slate-500
              focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500
              disabled:bg-slate-800/50 disabled:cursor-not-allowed
              ${hasToggle ? 'pr-10' : ''}
              ${error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : 'border-slate-700'}
              ${className}
            `}
            {...props}
            {...field.controlProps}
          />
          {hasToggle && (
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-white transition-colors rounded-r-md focus:outline-none focus-visible:text-white focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
              aria-label={passwordToggleLabel}
              aria-pressed={visible}
              aria-controls={inputId}
            >
              {visible ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        </div>
        <FieldMessage field={field} />
      </div>
    );
  }
);

Input.displayName = 'Input';
